// Galexia's dashboard-grade GenUI library. Composes the bundled
// `openuiLibrary` (Stack, Card, Modal, Form, Table, etc.) with custom
// chart components from `genui-charts.tsx` that expose `palette` as a
// positional arg — the bundled charts hide the underlying React `theme`
// prop, which means the model can't vary colors across charts in one
// dashboard. With these custom wrappers, model can write
// `BarChart(labels, series, "grouped", null, null, "sunset")` and get a
// distinct palette per chart.
//
// Used by:
// - `openui-dashboard-source.ts` (bake the system prompt at build time)
// - `app/labs/genui/client.tsx` DashboardPanel (render side)
// Both must reference the SAME library or the model emits code the
// renderer can't parse.

import { createLibrary } from '@openuidev/react-lang';
import type { Library, ComponentGroup } from '@openuidev/react-lang';
import { openuiLibrary, openuiPromptOptions } from '@openuidev/react-ui/genui-lib';
import { themedCharts, REPLACED_CHART_NAMES } from './genui-charts';

// Drop the bundled chart components and substitute our themed versions.
const baseComponents = Object.values(openuiLibrary.components).filter(
  (c) => !REPLACED_CHART_NAMES.has(c.name),
);

// Patch the chart group's notes so the prompt mentions the new palette
// arg explicitly. Any group that listed our replaced chart names gets a
// note appended; other groups pass through untouched.
const patchedGroups: ComponentGroup[] | undefined = openuiLibrary.componentGroups?.map((g) => {
  const overlapsCharts = g.components.some((n) => REPLACED_CHART_NAMES.has(n));
  if (!overlapsCharts) return g;
  const extra =
    "- Each chart accepts an optional last positional arg `palette` (one of: \"ocean\" | \"orchid\" | \"emerald\" | \"sunset\" | \"spectrum\" | \"vivid\"). Vary palettes across charts in the same dashboard so the result isn't all-blue. Match the topic when you can: ocean=finance, emerald=nature, sunset=energy, orchid=creative, spectrum=multi-category, vivid=high-contrast.";
  return {
    ...g,
    notes: [...(g.notes ?? []), extra],
  };
});

export const galexiaDashboardLibrary: Library = createLibrary({
  root: openuiLibrary.root ?? 'Stack',
  componentGroups: patchedGroups,
  components: [...baseComponents, ...themedCharts],
});

export const galexiaDashboardPromptOptions = openuiPromptOptions;
